import { FC, useEffect, useState } from "react";
import { InspectionToolSet } from "../../types/IInspectionToolSet";
import { IJob } from "../../types/IJob";
import { InspectionLog } from "../../types/InspectionLog";
import { IUserInfo } from "../../types/IUserInfo";
import { TestType } from "../../types/TestType";
import {
  ColumnDirective,
  ColumnsDirective,
  ContextMenu,
  ContextMenuItemModel,
  GridComponent,
  QueryCellInfoEventArgs,
  Resize,
  RowSelectEventArgs,
} from "@syncfusion/ej2-react-grids";
import { useAuth } from "../../utility/useAuth";
import {
  getDocuments,
  getInspectionToolSets,
  getJobInspectionLogs,
  getLeakTestCertificatesForProductionOrder,
  getOption,
  saveLeakTestCertificate,
} from "../../services/ApiService";
import { InspectionStatus } from "../../types/InspectionStatus";
import { ToastUtility } from "@syncfusion/ej2-react-notifications";
import { LeakTestCertificate } from "../../types/LeakTestCertificate";
import { Col, Row } from "react-grid-system";
import { getPathItem } from "../../utility/pathHelper";
import {
  LabelText,
  StyledCompletePressSetJob,
} from "./CompletePressSetJob.styles";
import { DropDownListComponent, Inject } from "@syncfusion/ej2-react-dropdowns";
import {
  NumericTextBoxComponent,
  TextBoxComponent,
} from "@syncfusion/ej2-react-inputs";
import {
  ButtonComponent,
  RadioButtonComponent,
} from "@syncfusion/ej2-react-buttons";
import { shortDateFormat } from "../../utility/dateHelpers";
import {
  FAILED,
  isSkipped,
  PASSED,
  printCertificate,
  savePdf,
  SKIPPED,
  updateDefaultValues,
  validateTest,
} from "../../utility/testHelper";
import { getUniquePartProductionOrderNumber } from "../../utility/partOrderHelper";
import { MenuEventArgs } from "@syncfusion/ej2-react-navigations";
import { TooltipComponent } from "@syncfusion/ej2-react-popups";

interface ICompleteJob {
  job: IJob | undefined;
  operation: string;
  user: IUserInfo;
  onValidate: (
    inspectionToolSet: InspectionToolSet | undefined,
    reason: string | undefined,
    operationStatus: string | undefined,
    inspectionLogs: InspectionLog[] | undefined,
  ) => void;
}

export const CompletePressSetJob: FC<ICompleteJob> = ({
  job,
  operation,
  user,
  onValidate,
}) => {
  const [inspectionToolSet, setInspectionToolSet] =
    useState<InspectionToolSet>();
  const [inspectionToolSets, setInspectionToolSets] =
    useState<InspectionToolSet[]>();
  const [inspectionLogs, setInspectionLogs] = useState<InspectionLog[]>();
  const [certificates, setCertificates] = useState<LeakTestCertificate[]>();
  const [certificate, setCertificate] = useState<LeakTestCertificate>();
  const [selectedCertificateIndex, setSelectedCertificateIndex] = useState<
    number | undefined
  >(undefined);
  const [testGas, setTestGas] = useState<string | null>();

  const [errors, setErrors] = useState<string>();
  const [disableToolSets, setDisableToolSets] = useState<boolean>(false);
  const [operationState, setOperationState] = useState<string>();
  const [printLocations, setPrintLocations] = useState<string[]>([]);
  const [selectedPrinterLocation, setSelectedPrinterLocation] =
    useState<string>();
  const [printerName, setPrintName] = useState<string>();

  let gridCert: GridComponent | null;

  const auth = useAuth();

  useEffect(() => {
    if (operation && job && job?.jobNumber) {
      const fetchData = async () => {
        // get the data from the api
        const toolSets = await getInspectionToolSets(auth, operation);
        setInspectionToolSets(toolSets);
        const rejectedInspectionLogs = await getJobInspectionLogs(
          auth,
          job.jobNumber,
          InspectionStatus.Rejected,
        );
        populateInspectionLogs(rejectedInspectionLogs, job);

        const certificates = (
          await getLeakTestCertificatesForProductionOrder(auth, job.jobNumber)
        ).filter(
          (c) =>
            c.testType === TestType[TestType.PressureSet] ||
            c.testType === TestType[TestType.SnoopTest],
        );

        const documents = await getDocuments(
          auth,
          undefined,
          "print-locations",
          true,
        );
        const locations = documents.map(
          (document) => getPathItem(document).fileName,
        );
        const defaultPrinter = await getOption(
          auth,
          `DocumentPrinter:${user.printLocation}`,
        );

        setPrintLocations(locations);
        setSelectedPrinterLocation(user.printLocation);
        setPrintName(defaultPrinter);

        populateCertificates(certificates, toolSets, job);
      };

      fetchData()
        // make sure to catch any error
        .catch((error) => {
          const message = `Failed to get data. Error: ${error}`;
          console.error(message);
          ToastUtility.show(message, "Error", 2000);
        });
    }
  }, [operation]);

  function populateInspectionLogs(inspectionLogs: InspectionLog[], job: IJob) {
    if (inspectionLogs && inspectionLogs.length > 0) {
      const logs = inspectionLogs.map((log) => {
        log.rework = true;
        return log;
      });
      setInspectionLogs(logs);
    } else {
      let logs: InspectionLog[] = [];
      for (let i = 1; i <= job.qtyOrdered; i++) {
        logs.push({
          id: undefined,
          job: job.jobNumber,
          operation: operation,
          line: i,
          associate: user.code,
          weldId: `${job.jobNumber}-${i}`,
          dateCreated: new Date(),
          status: "",
          isAccepted: true,
          comment: "",
          rework: false,
          note: undefined,
          rejectionReason: undefined,
        });
        setInspectionLogs(logs);
      }
    }
  }

  function populateCertificates(
    leakTestCerts: LeakTestCertificate[] | undefined,
    toolSets: InspectionToolSet[],
    job: IJob,
  ) {
    const selectedStation = leakTestCerts?.find((c) => c.id > 0)?.station;
    const selectedToolSetId = leakTestCerts?.find(
      (c) => c.id > 0,
    )?.inspectionToolSetId;

    if (leakTestCerts && leakTestCerts.length) {
      const toolSet = toolSets.find((tool) => tool.name === selectedStation);
      setInspectionToolSet(toolSet);
      setDisableToolSets(true);
    }

    let certificates: LeakTestCertificate[] = [];
    for (let i = 1; i <= job.qtyOrdered; i++) {
      const partProductionOrderNumber = `${job.jobNumber}-${i}`;
      const certificate = leakTestCerts?.find(
        (c) => c.partProductionOrderNumber === partProductionOrderNumber,
      );
      if (certificate) {
        validate(certificate);
        certificates.push(certificate);
      } else {
        certificates.push({
          id: 0,
          productionOrderNumber: job.jobNumber,
          partProductionOrderNumber: partProductionOrderNumber,
          issue: job.description,
          testType: TestType[TestType.PressureSet],
          leakTwo: 10,
          date: new Date(),
          customer: job.customerName,
          partNumber: job.stockCode,
          testDuration: 10,
          testedBy: user.name,
          certificateNumber: undefined,
          leakRate: 0,
          exp: 0,
          testGas: undefined,
          bubbleTestType: "SetPressure",
          testPressure: 0,
          serialNumber: "",
          passed: true,
          status: "",
          station: selectedStation ?? "",
          reportParameters: "",
          customerPurchaseOrderNumber: job.customerPONumber,
          acceptableRate: 5,
          acceptableExp: -9,
          inspectionToolSetId: selectedToolSetId,
          springKit: "",
          temperature: 0,
        });
      }
    }
    setCertificates(certificates);
  }

  useEffect(() => {
    let operationStatus: string | undefined;
    let logs: InspectionLog[] | undefined;
    if (certificates) {
      if (
        certificates
          .filter((c) => !(isSkipped(c)))
          .some(
            (certificate) => certificate.id > 0 && certificate.passed === false,
          )
      ) {
        operationStatus = InspectionStatus[InspectionStatus.Rejected];
      }

      if (
        certificates
          .filter((c) => !(isSkipped(c)))
          .every(
            (certificate) => certificate.id > 0 && certificate.passed === true,
          )
      ) {
        operationStatus = InspectionStatus[InspectionStatus.Accepted];
      }
      if (inspectionLogs) {
        logs = updateInspectionLogs(inspectionLogs, certificates);
      }
    }

    setOperationState(operationStatus);
    onValidate(inspectionToolSet, "", operationStatus, logs ?? inspectionLogs);
  }, [inspectionToolSet, inspectionLogs, certificates]);

  function updateInspectionLogs(
    inspectionLogs: InspectionLog[],
    certificates: LeakTestCertificate[],
  ): InspectionLog[] {
    const logs = inspectionLogs?.map((log) => {
      const cert = certificates?.find(
        (certificate) =>
          certificate.id > 0 &&
          certificate.partProductionOrderNumber === log.weldId &&
          (certificate.passed === true || certificate.passed === false),
      );
      if (cert) {
        log.status = cert.passed
          ? InspectionStatus[InspectionStatus.Accepted]
          : InspectionStatus[InspectionStatus.Rejected];
      }
      return log;
    });
    return logs;
  }

  function updateCertificates(certificate: LeakTestCertificate) {
    if (certificate) {
      certificate = validate(certificate);
      setCertificate(certificate);

      if (certificates) {
        const index = certificates.findIndex(
          (c) =>
            c.partProductionOrderNumber ===
            certificate.partProductionOrderNumber,
        );

        if (index >= 0) {
          let certs = [...certificates];
          certs[index] = certificate;

          setCertificates([...certs]);
          setSelectedCertificateIndex(index);
          gridCert?.refresh();
          gridCert?.selectRow(index);
          if (inspectionLogs && certs) {
            const logs = updateInspectionLogs(inspectionLogs, certs);
            setInspectionLogs(logs);
          }
        } else {
          console.warn(
            `Unable to select certificate row, index not found: ${certificate.partProductionOrderNumber}`,
          );
        }
      }
    }
  }

  function onToolSetChange(args: any) {
    if (args) {
      const toolSet = inspectionToolSets?.find(
        (tool) => tool.name === args.value,
      );
      setInspectionToolSet(toolSet);
      if (certificates) {
        const certs = certificates.map((c) => {
          c.station = args.value;
          c.inspectionToolSetId = toolSet?.id;
          return c;
        });
        setCertificates(certs);
      }

      if (certificate) {
        certificate.station = args.value;
        setCertificate(certificate);
        validate(certificate);
      }
    }
  }

  function validate(certificate: LeakTestCertificate): LeakTestCertificate {
    if (certificate) {
      const error = validateTest(certificate);
      setErrors(error);
    }
    return certificate;
  }

  function inspectionToolSetTemplate(data: any): JSX.Element {
    return (
      <Row align="stretch">
        <Col>
          <span>{data.name}</span>
        </Col>
        <Col>
          <div
            style={{
              borderStyle: "solid",
              backgroundColor: data.colour.replace("#FF", "#").toLowerCase(),
              minWidth: 100,
              height: 20,
            }}
          ></div>
        </Col>
      </Row>
    );
  }

  function onCertificateSelected(args: RowSelectEventArgs) {
    if (args.rowIndex !== undefined && certificates) {
      updateDefaultValues(certificates[args.rowIndex], certificates);
      validate(certificates[args.rowIndex]);
      setCertificate(certificates[args.rowIndex]);
      setTestGas(certificates[args.rowIndex]?.testGas ?? null);
    }
  }

  function onTestGasChange(args: any) {
    if (args && certificate) {
      certificate.testGas = args.value;
      setTestGas(certificate.testGas);
      setCertificate(certificate);
      updateCertificates(certificate);
    }
  }

  function onTestPressureChange(args: any) {
    if (args && certificate) {
      certificate.testPressure = args.value;

      setCertificate(certificate);
      updateCertificates(certificate);
    }
  }

  function onTestTemperatureChange(args: any) {
    if (args && certificate) {
      certificate.temperature = args.value;

      setCertificate(certificate);
      updateCertificates(certificate);
    }
  }
  const onSpringKitChange = (event: any) => {
    if (event && certificate) {
      certificate.springKit = event.value;

      setCertificate(certificate);
      updateCertificates(certificate);
    }
  };

  function onLocationChange(args: any) {
    if (args) {
      setSelectedPrinterLocation(args.value);
      getOption(auth, `DocumentPrinter:${args.value}`).then((option) => {
        setPrintName(option);
      });
    }
  }

  function onTestPassed(args: any) {
    if (certificate && args /* && args.target.checked === true */) {
      certificate.status = args.target.checked ?  PASSED : FAILED;
      console.log(
        `onTestPassed: ${args.target.checked} ${JSON.stringify(certificate)}`,
      );
      setCertificate(certificate);
      updateCertificates(certificate);
    }
  }

  function onTestFailed(args: any) {
    if (certificate && args && args.target.checked === true) {
      certificate.status = FAILED;
      console.log(
        `onTestFailed: ${args.target.checked} ${JSON.stringify(certificate)}`,
      );
      setCertificate(certificate);
      updateCertificates(certificate);
    }
  }

  function onTestSkipped(args: any) {
    if (certificate && args && args.target.checked === true) {
      certificate.status = SKIPPED;
      console.log(
        `onTestSkipped: ${args.target.checked} ${JSON.stringify(certificate)}`,
      );
      setCertificate(certificate);
      updateCertificates(certificate);
    }
  }

  function saveCertificate(certificate: LeakTestCertificate | undefined) {
    console.log(`save certificate: ${JSON.stringify(certificate)}`);
    if (certificate) {
      saveLeakTestCertificate(auth, certificate)
        .then((result) => {
          setCertificate(result);
          updateCertificates(result);
        })
        .catch((error) => {
          const message = `Failed to save the leak test certificate. Error: ${error}`;
          console.error(message);
          ToastUtility.show(message, "Error", 2000);
        });
    }
  }

  const testGases: { [key: string]: Object }[] = [
    { value: "Nitrogen", displayName: "Nitrogen" },
    { value: "Helium", displayName: "Helium" },
    { value: "Air", displayName: "Air" },
  ];

  const contextMenuItems: ContextMenuItemModel[] = [
    { text: "Duplicate", target: ".e-content", id: "duplicate" },
  ];

  function duplicateTest(selectedTest: LeakTestCertificate) {
    if (!job) {
      return;
    }

    const savedStation = certificates?.find((c) => c.id > 0)?.station;
    const savedToolSetId = certificates?.find(
      (c) => c.id > 0,
    )?.inspectionToolSetId;

    const sorted = getUniquePartProductionOrderNumber(
      selectedTest.partProductionOrderNumber,
      certificates?.map((c) => c.partProductionOrderNumber) ?? [],
    );

    const weldId = sorted[0];
    const index = sorted[1];

    const cert: LeakTestCertificate = {
      id: 0,
      productionOrderNumber: job.jobNumber,
      partProductionOrderNumber: weldId,
      issue: job.description,
      testType: TestType[TestType.PressureSet],
      leakTwo: 10,
      date: new Date(),
      customer: job.customerName,
      partNumber: job.stockCode,
      testDuration: 10,
      testedBy: user.name,
      certificateNumber: undefined,
      leakRate: 0,
      exp: 0,
      testGas: undefined,
      bubbleTestType: "SetPressure",
      testPressure: 0,
      serialNumber: "",
      passed: true,
      status: "",
      station: savedStation ?? "",
      reportParameters: "",
      customerPurchaseOrderNumber: job.customerPONumber,
      acceptableRate: 5,
      acceptableExp: -9,
      inspectionToolSetId: savedToolSetId,
      springKit: "",
      temperature: 0,
    };

    if (inspectionLogs && certificates) {
      setCertificates([
        ...certificates.slice(0, index),
        cert,
        ...certificates.slice(index),
      ]);
    }
  }

  const contextMenuClick = (args: MenuEventArgs) => {
    if (gridCert && args.item.id === "duplicate") {
      const selectedTest = (
        gridCert.getSelectedRecords() as LeakTestCertificate[]
      )[0];

      duplicateTest(selectedTest);
    }
  };

  const getCertificate = (
    partProductionOrderNumber: string,
  ): LeakTestCertificate | undefined => {
    return certificates?.find(
      (c) => c.partProductionOrderNumber === partProductionOrderNumber,
    );
  };

  const customizeTestCell = (args: QueryCellInfoEventArgs) => {
    if (args.cell && args.data) {
      const component = args.data as LeakTestCertificate;
      const cert = getCertificate(component.partProductionOrderNumber);

      if (cert?.status === FAILED) {
        args.cell.classList.add("test-bad");
      } else if (cert?.status === PASSED) {
        args.cell.classList.add("test-ok");
      }
    }
  };

  const canSaveCert = (partProductionOrderNumber: string): boolean => {
    const cert = certificates?.find(
      (c) => c.partProductionOrderNumber === partProductionOrderNumber,
    );
    return (
      (cert &&
        validateTest(cert)?.length === 0 &&
        (cert.id < 1 || cert?.passed !== true)) ??
      false
    );
  };

  const testBtnsTemplate = (props: LeakTestCertificate) => {
    return (
      <div>
        <TooltipComponent content="Duplicate test">
          <ButtonComponent
            iconCss="e-icons e-copy"
            onClick={() => {
              duplicateTest(props);
            }}
          />
        </TooltipComponent>
        <TooltipComponent content="Save test">
          <ButtonComponent
            disabled={!canSaveCert(props.partProductionOrderNumber)}
            iconCss="e-icons e-save"
            onClick={() => {
              saveCertificate(getCertificate(props.partProductionOrderNumber));
            }}
          />
        </TooltipComponent>
        <TooltipComponent content="Print test">
          <ButtonComponent
            disabled={!props.id || !printerName || !selectedPrinterLocation}
            iconCss="e-icons e-print"
            onClick={() => {
              printCertificate(
                auth,
                getCertificate(props.partProductionOrderNumber),
                printerName!,
                selectedPrinterLocation!,
              );
            }}
          />
        </TooltipComponent>
        <TooltipComponent content="Save as PDF">
          <ButtonComponent
            disabled={!props.id}
            iconCss="e-icons e-export-pdf"
            onClick={() => {
              savePdf(auth, getCertificate(props.partProductionOrderNumber));
            }}
          />
        </TooltipComponent>
      </div>
    );
  };

  return (
    <StyledCompletePressSetJob>
      <Row align="stretch">
        <Col sm={4}>
          <LabelText>Select inspection tool set:</LabelText>
        </Col>
        <Col>
          <DropDownListComponent
            enabled={!disableToolSets}
            dataSource={inspectionToolSets}
            fields={{ text: "name", value: "name" }}
            valueTemplate={inspectionToolSetTemplate}
            itemTemplate={inspectionToolSetTemplate}
            value={inspectionToolSet?.name}
            onChange={onToolSetChange}
          />
        </Col>
      </Row>
      <Row align="stretch">
        <Col sm={4}>
          <LabelText>Customer:</LabelText>
        </Col>
        <Col>
          <TextBoxComponent value={job?.customerName} readOnly />
        </Col>
      </Row>
      <Row align="stretch">
        <Col sm={4}>
          <LabelText>Part Number:</LabelText>
        </Col>
        <Col>
          <TextBoxComponent value={job?.stockCode} readOnly />
        </Col>
      </Row>
      <Row align="stretch">
        <Col sm={6}>
          <GridComponent
            id="Grid"
            ref={(g: GridComponent) => (gridCert = g)}
            dataSource={certificates}
            selectionSettings={{
              mode: "Row",
              type: "Single",
              persistSelection: true,
            }}
            selectedRowIndex={selectedCertificateIndex}
            allowResizing
            height={320}
            gridLines="Both"
            rowSelected={onCertificateSelected}
            allowSelection={true}
            contextMenuItems={contextMenuItems}
            contextMenuClick={contextMenuClick}
            queryCellInfo={customizeTestCell}
          >
            <Inject services={[Resize, ContextMenu]} />
            <ColumnsDirective>
              <ColumnDirective
                field="partProductionOrderNumber"
                isPrimaryKey={true}
                headerText="Part Production Order"
                width="150"
                textAlign="Left"
              />
              <ColumnDirective
                field="status"
                //template={testResultTemplate}
                headerText="Test Result"
                width="100"
                textAlign="Left"
              />
              <ColumnDirective
                isPrimaryKey={true}
                headerText=""
                width="50"
                textAlign="Left"
                template={testBtnsTemplate}
              />
            </ColumnsDirective>
          </GridComponent>
        </Col>
        {certificate && (
          <Col>
            <Row align="stretch">
              <Col sm={6}>
                <LabelText>Certificate Number</LabelText>
              </Col>
              <Col>
                <TextBoxComponent
                  value={
                    certificate?.certificateNumber
                      ?.toString()
                      ?.padStart(6, "0") ?? ""
                  }
                  readOnly
                />
              </Col>
            </Row>
            <Row align="stretch">
              <Col sm={6}>
                <LabelText>Date of Test</LabelText>
              </Col>
              <Col>
                <TextBoxComponent
                  value={shortDateFormat(certificate?.date?.toString())}
                  readOnly
                />
              </Col>
            </Row>
            <Row align="stretch">
              <Col sm={6}>
                <LabelText>Test Fluid</LabelText>
              </Col>
              <Col>
                {certificate.bubbleTestType === "Hydrostatic" ? (
                  <TextBoxComponent value="Water" readOnly />
                ) : (
                  <DropDownListComponent
                    value={testGas}
                    fields={{ text: "displayName", value: "value" }}
                    dataSource={testGases}
                    onChange={onTestGasChange}
                    enabled={
                      (!((certificate?.certificateNumber ?? 0) > 0) ||
                        certificate?.passed !== true) ??
                      true
                    }
                  />
                )}
              </Col>
            </Row>
            <Row align="stretch">
              <Col sm={6}>
                <LabelText>Leak Test Type</LabelText>
              </Col>
              <Col>
                <TextBoxComponent value="Set Pressure" readOnly />
              </Col>
            </Row>
            <Row align="stretch">
              <Col sm={6}>
                <LabelText>Pressure relief set to Bar(g)</LabelText>
              </Col>
              <Col>
                <NumericTextBoxComponent
                  value={certificate?.testPressure}
                  onChange={onTestPressureChange}
                  enabled={
                    (!((certificate?.certificateNumber ?? 0) > 0) ||
                      certificate?.passed !== true) ??
                    true
                  }
                />
              </Col>
            </Row>
            <Row align="stretch">
              <Col sm={6}>
                <LabelText>Spring Kit Number</LabelText>
              </Col>
              <Col>
                <TextBoxComponent
                  value={certificate?.springKit}
                  input={onSpringKitChange}
                  enabled={
                    (!((certificate?.certificateNumber ?? 0) > 0) ||
                      certificate?.passed !== true) ??
                    true
                  }
                />
              </Col>
            </Row>
            <Row align="stretch">
              <Col sm={6}>
                <LabelText>Temperature</LabelText>
              </Col>
              <Col>
                <NumericTextBoxComponent
                  value={certificate?.temperature}
                  onChange={onTestTemperatureChange}
                  enabled={
                    (!((certificate?.certificateNumber ?? 0) > 0) ||
                      certificate?.passed !== true) ??
                    true
                  }
                />
              </Col>
            </Row>
            <Row align="stretch">
              <Col sm={3}>
                <LabelText>Test Result</LabelText>
              </Col>
              <Col sm={3}>
                <RadioButtonComponent
                  label="Passed"
                  name="testResult"
                  checked={
                    certificate?.status === PASSED
                  }
                  onChange={onTestPassed}
                  disabled={
                    ((certificate?.certificateNumber ?? 0) > 0 &&
                      certificate?.passed === true) ??
                    false
                  }
                />
              </Col>
              <Col sm={3}>
                <RadioButtonComponent
                  label="Failed"
                  name="testResult"
                  checked={
                    certificate?.status === FAILED
                  }
                  onChange={onTestFailed}
                  disabled={
                    ((certificate?.certificateNumber ?? 0) > 0 &&
                      certificate?.passed === true) ??
                    false
                  }
                />
              </Col>
              <Col sm={2}>
                <RadioButtonComponent
                  label="Skipped"
                  name="testResult"
                  checked={certificate?.status === SKIPPED}
                  disabled={
                    ((certificate?.certificateNumber ?? 0) > 0 &&
                      certificate?.passed === true) ??
                    false
                  }
                  onChange={onTestSkipped}
                />
              </Col>
            </Row>
            <Row align="stretch">
              <Col sm={6}>
                <LabelText>Tested By</LabelText>
              </Col>
              <Col>
                <TextBoxComponent value={certificate?.testedBy} readOnly />
              </Col>
            </Row>
            <Row align="stretch">
              <Col sm={6}>
                <LabelText>Print Location</LabelText>
              </Col>
              <Col>
                <DropDownListComponent
                  id="printLocElement"
                  dataSource={printLocations}
                  value={selectedPrinterLocation}
                  placeholder="Select a printer location"
                  onChange={onLocationChange}
                />
              </Col>
            </Row>
            <Row align="stretch">
              <h6 style={{ color: "red" }}>{errors}</h6>
            </Row>
          </Col>
        )}
      </Row>
      <Row align="stretch">
        <Col sm={4}>
          <LabelText>Operation Result:</LabelText>
        </Col>
        <Col>
          <TextBoxComponent value={operationState} readOnly />
        </Col>
      </Row>
    </StyledCompletePressSetJob>
  );
};
